import { useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, BACKEND_URL } from "@/lib/api";
import { Search, X, MapPin, Printer, Download, Loader2 } from "lucide-react";

// --- Global Stock Search ---
export function StockSearch({ onNavigate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/admin/stock/search?q=${encodeURIComponent(val)}`);
      setResults(r.data.results);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div className="hg-card p-4 mb-4">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input value={q} onChange={e => doSearch(e.target.value)} data-testid="stock-search-input"
          placeholder="Kërko produkt sipas emrit, SKU-së ose numrit të artikullit..."
          className="w-full h-11 bg-[#131E30] border border-[#1F2B44] pl-10 pr-9" />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X size={16} className="text-neutral-500" />
          </button>
        )}
        {loading && <Loader2 size={14} className="absolute right-10 top-1/2 -translate-y-1/2 animate-spin text-neutral-500" />}
      </div>
      {results.length > 0 && (
        <div className="mt-3 max-h-96 overflow-y-auto space-y-2" data-testid="stock-search-results">
          {results.map(r => (
            <div key={r.product_id} className="border border-[#1F2B44] p-3 flex gap-3 items-start hover:bg-[#0f0f0f]">
              {r.image && <img src={mediaUrl(r.image)} alt="" className="w-14 h-14 object-contain bg-[#131E30] border border-[#1F2B44] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-neutral-500 flex gap-3">
                  {r.sku && <span>SKU: {r.sku}</span>}
                  {r.article_number && <span>Art: {r.article_number}</span>}
                  <span className="text-emerald-400 font-bold">Total: {r.total_qty} copë</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.locations.length === 0 && <span className="text-xs text-neutral-500">Pa lokacion</span>}
                  {r.locations.map((l, i) => (
                    <button key={i} onClick={() => onNavigate({ cabinet: l.cabinet, drawer: l.drawer, position: l.position })}
                      data-testid={`search-loc-${r.product_id}-${i}`}
                      className="text-xs bg-[#FFD600]/15 text-[#FFD600] border border-[#FFD600]/40 px-2 py-1 hover:bg-[#FFD600]/25 inline-flex items-center gap-1 font-mono">
                      <MapPin size={11} /> {l.cabinet}-{l.drawer}-{l.position} • {l.qty}
                    </button>
                  ))}
                </div>
              </div>
              <Link to={`/produkt/${r.product_id}`} className="text-xs text-[#FFD600] hover:underline shrink-0">Hap</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- HTML escape helper ---
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// --- QR Print Sheet (for a drawer) ---
export function QRPrintButton({ cabinet, drawer, positions }) {
  const openPrint = () => {
    const rows = positions.map(p => {
      const code = `HG|${cabinet}|${drawer}|${p.position}`;
      const url = `${BACKEND_URL}/api/admin/qr?data=${encodeURIComponent(code)}&size=8`;
      return `
        <div class="lbl">
          <img src="${url}" alt="qr" crossorigin="anonymous" />
          <div class="loc">${esc(cabinet)}-${esc(drawer)}</div>
          <div class="pos">${esc(p.position)}</div>
          ${p.product_name ? `<div class="prod">${esc(p.product_name)}</div>` : `<div class="prod empty">— i lirë —</div>`}
        </div>
      `;
    }).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <!doctype html><html><head><title>QR - ${cabinet}-${drawer}</title>
      <style>
        @page { size: A4; margin: 8mm; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 8px; background: white; color: black; }
        h1 { font-size: 14pt; margin: 0 0 6px; }
        .info { font-size: 9pt; color: #555; margin-bottom: 12px; }
        .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
        .lbl { border: 1px solid #000; padding: 6px; text-align: center; page-break-inside: avoid; }
        .lbl img { width: 100%; max-width: 130px; height: auto; display: block; margin: 0 auto; }
        .loc { font-size: 8pt; color: #666; margin-top: 3px; }
        .pos { font-size: 14pt; font-weight: bold; margin: 2px 0; }
        .prod { font-size: 7pt; color: #333; line-height: 1.15; min-height: 20px; }
        .prod.empty { color: #999; font-style: italic; }
        @media print { .no-print { display: none; } }
      </style></head><body>
      <button class="no-print" onclick="window.print()" style="padding:8px 16px; background:#FFD600; border:0; font-weight:bold; cursor:pointer; margin-bottom:8px">Printo</button>
      <h1>Etiketa QR — Dollapi ${cabinet}-${drawer}</h1>
      <div class="info">Skano me telefon për të hapur pozicionin direkt • ${positions.length} etiketa</div>
      <div class="grid">${rows}</div>
      <script>
        // Auto-open print dialog when all images have loaded
        window.addEventListener('load', function() {
          const imgs = document.querySelectorAll('img');
          let loaded = 0;
          if (imgs.length === 0) { setTimeout(() => window.print(), 300); return; }
          imgs.forEach(img => {
            const done = () => { loaded++; if (loaded === imgs.length) setTimeout(() => window.print(), 300); };
            if (img.complete) done(); else { img.addEventListener('load', done); img.addEventListener('error', done); }
          });
        });
      </script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <button onClick={openPrint} data-testid="qr-print-btn"
      className="text-xs border border-[#1F2B44] px-3 py-1.5 hover:bg-[#262626] inline-flex items-center gap-1">
      <Printer size={12} /> Printo QR
    </button>
  );
}

// --- Location display helper ---
// Format "A03-03-A-01" → "A3-3-1-A1"
// cabinet "A03" → "A3", drawer "03" → "3", position "A-01" → level "1" (col unpadded), pos "A1" (row+col_unpadded)
export function formatLocDisplay(cabinet, drawer, position) {
  const cab = String(cabinet || "").replace(/^([A-Z]+)0*(\d+)$/i, "$1$2") || String(cabinet || "");
  const drw = String(parseInt(drawer, 10) || drawer || "");
  const pos = String(position || "");
  const m = pos.match(/^([A-Z]+)-?(\d+)$/i);
  if (!m) return `${cab}-${drw}-${pos}`;
  const row = m[1].toUpperCase();
  const col = String(parseInt(m[2], 10));
  return `${cab}-${drw}-${col}-${row}${col}`;
}

// Parse a full shelf_location string like "A03-03-A-01" → "A3-3-1-A1"
export function formatLocString(s) {
  if (!s) return "";
  const str = String(s).trim();
  // Try 4-part format: CAB-DRW-ROW-COL (e.g. A03-03-A-01)
  const m4 = str.match(/^([A-Z]+\d+)-(\d+)-([A-Z]+)-?(\d+)$/i);
  if (m4) return formatLocDisplay(m4[1], m4[2], `${m4[3]}-${m4[4]}`);
  // Try 3-part: CAB-DRW-POS (e.g. A03-03-A01)
  const m3 = str.match(/^([A-Z]+\d+)-(\d+)-([A-Z]+\d+)$/i);
  if (m3) return formatLocDisplay(m3[1], m3[2], m3[3]);
  return str;
}

// --- Single Position Ticket Print ---
export function printPositionTicket({ cabinet, drawer, position, productInfo }) {
  const article = productInfo?.article || productInfo?.sku || "";
  const placementCode = productInfo && article
    ? `HGP|${article}|${cabinet}|${drawer}|${position}`
    : `HG|${cabinet}|${drawer}|${position}`;
  const removeCode = article ? `HGR|${article}` : null;
  const qrUrl = (data, size = 10) => `${BACKEND_URL}/api/admin/qr?data=${encodeURIComponent(data)}&size=${size}`;

  const placementBlock = `
    <div class="ticket ticket-place">
      <div class="type-tag">STOK PLAS ${esc(cabinet)}-${esc(drawer)}-${esc(position)}</div>
      <div class="tk-body">
        <img src="${qrUrl(placementCode)}" class="qr" />
        <div class="tk-info">
          <div class="tk-loc">📦 ${esc(cabinet)}-${esc(drawer)}-${esc(position)}</div>
          ${productInfo ? `
            <div class="tk-title">${esc(productInfo.name || "")}</div>
            <div class="tk-art">Art. Nr: <b>${esc(article)}</b></div>
          ` : `<div class="tk-hint">Pozicion i lirë — skano për të vendosur stok këtu</div>`}
        </div>
      </div>
    </div>
  `;
  const removeBlock = productInfo && removeCode ? `
    <div class="ticket ticket-remove">
      <div class="type-tag">DORËZIM • Artikull ${esc(article)}</div>
      <div class="tk-body">
        <img src="${qrUrl(removeCode)}" class="qr" />
        <div class="tk-info">
          <div class="tk-title">${esc(productInfo.name || "")}</div>
          <div class="tk-art">Art. Nr: <b>${esc(article)}</b></div>
          <div class="tk-hint">Skano për të nxjerrë stokun</div>
        </div>
      </div>
    </div>
  ` : "";
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`
    <!doctype html><html><head><title>Tiketa ${esc(cabinet)}-${esc(drawer)}-${esc(position)}</title>
    <style>
      @page { size: A4; margin: 10mm; }
      body { font-family: Arial, sans-serif; margin: 0; padding: 10px; background: white; color: black; }
      .ticket { border: 3px dashed #000; margin-bottom: 12px; page-break-inside: avoid; }
      .ticket-place { border-color: #059669; }
      .ticket-remove { border-color: #dc2626; }
      .type-tag { padding: 8px 12px; font-size: 12pt; font-weight: bold; letter-spacing: 1px; color: #fff; }
      .ticket-place .type-tag { background: #059669; }
      .ticket-remove .type-tag { background: #dc2626; }
      .tk-body { display: flex; gap: 16px; padding: 16px; align-items: center; }
      .qr { width: 200px; height: 200px; }
      .tk-info { flex: 1; }
      .tk-loc { font-size: 22pt; font-family: monospace; font-weight: bold; color: #059669; margin-bottom: 8px; }
      .tk-title { font-size: 16pt; font-weight: bold; margin-bottom: 4px; }
      .tk-art { font-size: 13pt; margin-bottom: 4px; }
      .tk-hint { font-size: 10pt; color: #666; font-style: italic; }
      @media print { .no-print { display: none; } }
    </style></head><body>
    <button class="no-print" onclick="window.print()" style="padding:8px 16px; background:#FFD600; border:0; font-weight:bold; cursor:pointer; margin-bottom:8px">Printo</button>
    ${placementBlock}
    ${removeBlock}
    <script>
      window.addEventListener('load', function() {
        const imgs = document.querySelectorAll('img');
        let loaded = 0;
        if (imgs.length === 0) { setTimeout(() => window.print(), 300); return; }
        imgs.forEach(img => {
          const done = () => { loaded++; if (loaded === imgs.length) setTimeout(() => window.print(), 300); };
          if (img.complete) done(); else { img.addEventListener('load', done); img.addEventListener('error', done); }
        });
      });
    </script>
    </body></html>
  `);
  w.document.close();
}

// --- Excel Export ---
export function ExcelExportButton() {
  const [open, setOpen] = useState(false);
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [loading, setLoading] = useState(false);

  const download = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (df) params.set("date_from", df);
      if (dt) params.set("date_to", dt);
      const token = localStorage.getItem("hg_token");
      const resp = await fetch(`${BACKEND_URL}/api/admin/stock/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stoku_levizje_${df || "start"}_${dt || "sot"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="export-btn"
        className="border-2 border-emerald-600 text-emerald-400 px-3 py-2 uppercase text-xs inline-flex items-center gap-2 hover:bg-emerald-900/40">
        <Download size={14} /> Eksporto Excel
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="export-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setOpen(false)} />
          <div className="relative bg-[#0B1220] border border-[#1F2B44] w-full max-w-md">
            <div className="border-b border-[#1F2B44] px-4 py-3 flex items-center justify-between">
              <div className="font-display text-lg uppercase">Eksporto Excel</div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-widest">Nga data</label>
                  <input type="date" value={df} onChange={e => setDf(e.target.value)} data-testid="export-date-from"
                    className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3" />
                </div>
                <div>
                  <label className="text-xs uppercase text-neutral-400 tracking-widest">Deri</label>
                  <input type="date" value={dt} onChange={e => setDt(e.target.value)} data-testid="export-date-to"
                    className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3" />
                </div>
              </div>
              <div className="text-[11px] text-neutral-500">Nëse i lini bosh, do të eksportohen të gjitha lëvizjet.</div>
              <button onClick={download} disabled={loading} data-testid="export-download"
                className="hg-btn-primary w-full h-11 uppercase text-xs inline-flex items-center justify-center gap-2">
                <Download size={14} /> {loading ? "Duke gjeneruar..." : "Shkarko .xlsx"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
